// src/pages/SinhBoSoQr/index.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import { toast } from 'react-toastify';
import BreadCrumb from 'Components/Common/BreadCrumb';
import ReasonPromptModal from 'Components/Common/ReasonPromptModal';
import KyPhatHanhList from './KyPhatHanhList';
import ConfirmSinhBoSoDialog from './modals/ConfirmSinhBoSoDialog';
import { fetchKyPhatHanhList, sinhBoSoQr } from 'slices/sinhBoSoQr/thunk';
import {
  clearFilters,
  closeModal,
  openModal,
  setFilters,
  setPage,
  setPageSize,
  setSelectedKy,
} from 'slices/sinhBoSoQr/reducer';
import { KyPhatHanh } from './types';
import { useBrandingTitle } from '../../hooks';
import { useCancelQrGenerationJob } from '../../hooks/useCancelQrGenerationJob';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('sinh_bo_so_qr');

const SinhBoSoQrPage: React.FC = () => {
  useBrandingTitle(st('sinh_bo_so_ma_qr_text'));

  const dispatch = useDispatch();
  const cancelQrGenerationJob = useCancelQrGenerationJob();
  const [searchParams] = useSearchParams();
  const [cancelTarget, setCancelTarget] = useState<{ ky: KyPhatHanh; jobId: string } | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const {
    kyPhatHanhList,
    selectedKy,
    activeJobs,
    loading,
    jobLoading,
    modalState,
    pagination,
    filters,
  } = useSelector((state: any) => state.SinhBoSoQr);

  const loadKyPhatHanhList = useCallback(() => {
    const params = {
      pageIndex: pagination.currentPage,
      pageSize: pagination.pageSize,
      search: filters.searchTerm,
      trangThaiQR: filters.trangThaiQR === 'All' ? undefined : filters.trangThaiQR,
    };
    dispatch(fetchKyPhatHanhList(params) as any);
  }, [dispatch, pagination.currentPage, pagination.pageSize, filters.searchTerm, filters.trangThaiQR]);

  useEffect(() => {
    loadKyPhatHanhList();
  }, [loadKyPhatHanhList]);

  // Nhan planCode tu man IssuancePlan dieu huong toi (sau khi duyet 1 ke hoach) de tu dong loc
  // dung ky vua duyet, khong bat nguoi dung phai tu tim lai trong danh sach.
  useEffect(() => {
    const planCode = searchParams.get('planCode');
    if (planCode) {
      dispatch(setFilters({ searchTerm: planCode }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenConfirm = useCallback((ky: KyPhatHanh) => {
    dispatch(setSelectedKy(ky));
    dispatch(openModal('confirm'));
  }, [dispatch]);

  const handleConfirmSinh = useCallback(() => {
    if (!selectedKy) return;
    dispatch(closeModal('confirm'));
    dispatch(sinhBoSoQr(selectedKy.id) as any);
  }, [dispatch, selectedKy]);

  const handlePageChange = (page: number) => {
    dispatch(setPage(page));
  };

  const handlePageSizeChange = (pageSize: number) => {
    dispatch(setPageSize(pageSize));
  };

  const handleFilterChange = (filterName: string, value: any) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  // README.md muc 8 — huy job sinh bo so & QR ngay tu cot Thao tac cua man nay (truoc day chi huy
  // duoc qua popup noi GlobalJobProgress). Sau khi huy xong, redirect sang Ke hoach phat hanh (khac
  // voi huy tu chinh trang do — chi can reload tai cho, xem handleCancelActiveJob trong
  // pages/IssuancePlan/index.tsx) vi ky phat hanh vua huy thuoc pham vi quan ly cua man do.
  const handleCancelJob = useCallback((ky: KyPhatHanh, jobId: string) => {
    setCancelTarget({ ky, jobId });
  }, []);

  // README.md muc 13 — dung chung logic huy voi popup tien trinh noi (Layouts/GlobalJobProgress.tsx,
  // QrJobTracker) qua useCancelQrGenerationJob, thay vi tu viet rieng — dam bao ca 2 noi cung mot
  // hanh vi (dieu huong sang issuance-plans ngay sau khi huy thanh cong). Chi phan xu ly LOI la dac
  // thu rieng cua trang nay: bao loi + tai lai danh sach TAI CHO (khong dieu huong di dau).
  const handleConfirmCancelJob = useCallback(async (reason: string) => {
    if (!cancelTarget || cancelling) return;
    setCancelling(true);
    try {
      await cancelQrGenerationJob(cancelTarget.jobId, reason, cancelTarget.ky.id);
      setCancelTarget(null);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_huy_job_sinh_bo_message'));
      loadKyPhatHanhList();
    } finally {
      setCancelling(false);
    }
  }, [cancelTarget, cancelling, cancelQrGenerationJob, loadKyPhatHanhList]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('sinh_bo_so_ma_qr_text')} pageTitle={st('quan_ly_ve_so_text')} />

          <Row>
            <Col lg={12}>
              <KyPhatHanhList
                data={kyPhatHanhList}
                activeJobs={activeJobs}
                loading={loading}
                pagination={pagination}
                filters={filters}
                onSinh={handleOpenConfirm}
                onCancelJob={handleCancelJob}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmSinhBoSoDialog
        isOpen={modalState.confirm}
        kyPhatHanh={selectedKy}
        loading={jobLoading}
        onConfirm={handleConfirmSinh}
        onClose={() => dispatch(closeModal('confirm'))}
      />

      <ReasonPromptModal
        isOpen={!!cancelTarget}
        title={st('huy_sinh_bo_so_qr_value_text', { value: cancelTarget?.ky.periodName || '' })}
        label={st('ly_do_huy_label')}
        confirmLabel={st('huy_sinh_bo_so_qr_text')}
        submitting={cancelling}
        onSubmit={handleConfirmCancelJob}
        onCancel={() => setCancelTarget(null)}
      />
    </React.Fragment>
  );
};

export default SinhBoSoQrPage;
